<template>
  <div
    class="comment-item"
    :class="{
      'comment-item--reply': isReply,
      'comment-item--own': isOwn,
      'comment-item--reported': isReportedByMe,
    }"
    :id="`comment-${comment.id}`"
  >
    <!-- Header row: date (left) + menu (right) -->
    <div class="comment-item__header">
      <span class="comment-item__time">{{ formattedDate }}</span>

      <!-- 3-dots options menu (edit / delete / report) -->
      <div v-if="canEdit || canDelete || canReport" class="comment-item__menu" ref="menuEl">
        <button
          type="button"
          class="comment-item__menu-btn"
          :aria-expanded="showMenu"
          @click.stop="showMenu = !showMenu"
          title="Opțiuni"
        >
          <i class="mdi mdi-dots-vertical" aria-hidden="true"></i>
        </button>
        <div v-if="showMenu" class="comment-item__menu-dropdown">
          <!-- Edit — top-level only -->
          <button
            v-if="canEdit && !isReply"
            type="button"
            class="comment-item__menu-item"
            @click="openEdit"
          >
            <i class="mdi mdi-pencil" aria-hidden="true"></i> Editează
          </button>

          <!-- Delete -->
          <button
            v-if="canDelete"
            type="button"
            class="comment-item__menu-item comment-item__menu-item--danger"
            :disabled="isDeleting"
            @click="deleteComment"
          >
            <i class="mdi mdi-trash-can-outline" aria-hidden="true"></i>
            {{ isDeleting ? 'Se șterge...' : 'Șterge' }}
          </button>

          <!-- Report -->
          <button
            v-if="canReport"
            type="button"
            class="comment-item__menu-item"
            :class="{ 'comment-item__menu-item--reported': isReportedByMe }"
            :disabled="isReporting || isReportedByMe"
            @click="reportComment"
          >
            <i class="mdi mdi-flag-outline" aria-hidden="true"></i>
            {{ isReportedByMe ? 'Raportat' : (isReporting ? 'Se raportează...' : 'Raportează') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Option badge (separate row so long text doesn't break the header) -->
    <div v-if="comment.option" class="comment-item__meta">
      <span class="comment-item__option-badge">{{ comment.option.title }}</span>
    </div>

    <!-- Parent quote (shown for replies) -->
    <button
      v-if="comment.parent_preview"
      type="button"
      class="comment-item__parent-quote"
      @click="scrollToParent"
    >
      {{ comment.parent_preview }}
    </button>

    <!-- Rich-text content rendered safely (hidden while editing).
         Content is sanitized server-side (PollCommentController::sanitizeHtml).
         The .comment-content CSS class further constrains layout-breaking inline styles. -->
    <div
      v-if="!isEditing"
      class="comment-item__body comment-content"
      :class="{ 'comment-item__body--collapsed': isCollapsed }"
      ref="bodyEl"
      v-html="comment.text"
    ></div>

    <!-- "Citește mai mult" / "Restrânge" -->
    <button
      v-if="showReadMore && !isEditing"
      type="button"
      class="comment-item__read-more"
      @click="isCollapsed = !isCollapsed"
    >
      {{ isCollapsed ? 'Citește mai mult ↓' : 'Restrânge ↑' }}
    </button>

    <!-- Inline edit editor -->
    <div v-if="isEditing" class="comment-item__edit-editor">
      <RichTextEditor v-model="editText" />
      <div class="poll-comments__form-actions">
        <button type="button" class="poll-comments__cancel-btn" @click="cancelEdit">Anulează</button>
        <button
          type="button"
          class="poll-comments__submit-btn"
          :disabled="isEditSaving || isTextEmpty(editText)"
          @click="saveEdit"
        >
          {{ isEditSaving ? 'Se salvează...' : 'Salvează' }}
        </button>
      </div>
      <p v-if="editError" class="poll-comments__error">{{ editError }}</p>
    </div>

    <!-- Action bar: votes + reply -->
    <div class="comment-item__actions">
      <!-- Like -->
      <button
        type="button"
        class="comment-item__vote-btn"
        :class="{ 'comment-item__vote-btn--active': localUserVote === 'upvote' }"
        :disabled="isVoting"
        @click="handleVote('upvote')"
        title="Util"
      >
        <i class="mdi mdi-thumb-up" aria-hidden="true"></i> <span v-if="localLikesCount > 0">{{ localLikesCount }}</span>
      </button>

      <!-- Dislike -->
      <button
        type="button"
        class="comment-item__vote-btn comment-item__vote-btn--dislike"
        :class="{ 'comment-item__vote-btn--active-dislike': localUserVote === 'downvote' }"
        :disabled="isVoting"
        @click="handleVote('downvote')"
        title="Inutil"
      >
        <i class="mdi mdi-thumb-down" aria-hidden="true"></i> <span v-if="localDislikesCount > 0">{{ localDislikesCount }}</span>
      </button>

      <!-- Reply button -->
      <button
        v-if="canReply"
        type="button"
        class="comment-item__reply-btn"
        @click="$emit('reply', comment)"
      >
        Răspunde
      </button>
    </div>

    <!-- Brief vote error -->
    <p v-if="voteError" class="comment-item__vote-error">{{ voteError }}</p>
    <!-- Delete error -->
    <p v-if="deleteError" class="comment-item__vote-error">{{ deleteError }}</p>
    <!-- Report error -->
    <p v-if="reportError" class="comment-item__vote-error">{{ reportError }}</p>

    <!-- Inline reply editor (shown when replying to this comment) -->
    <div v-if="showReplyEditor" class="comment-item__reply-editor">
      <slot name="reply-editor"></slot>
    </div>
  </div>
</template>

<script>
import RichTextEditor from '../../../components/RichTextEditor.vue';
import recaptchaMixin from '../../../mixins/recaptchaMixin.js';

export default {
  name: 'CommentItem',

  components: { RichTextEditor },

  mixins: [recaptchaMixin],

  props: {
    comment: {
      type: Object,
      required: true,
    },
    pollId: {
      type: [Number, String],
      required: true,
    },
    canReply: {
      type: Boolean,
      default: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    isReply: {
      type: Boolean,
      default: false,
    },
    isOwn: {
      type: Boolean,
      default: false,
    },
    showReplyEditor: {
      type: Boolean,
      default: false,
    },
    currentUserId: {
      type: [Number, String],
      default: null,
    },
    isReportedByMe: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['reply', 'need-auth', 'edited', 'deleted', 'reported'],

  data() {
    return {
      isCollapsed: true,
      showReadMore: false,
      localLikesCount: this.comment.likes_count || 0,
      localDislikesCount: this.comment.dislikes_count || 0,
      localUserVote: this.comment.user_vote || null,
      isVoting: false,
      voteError: null,
      // Options menu
      showMenu: false,
      // Edit state
      isEditing: false,
      editText: '',
      isEditSaving: false,
      editError: null,
      // Delete state
      isDeleting: false,
      deleteError: null,
      // Report state
      isReporting: false,
      reportError: null,
    };
  },

  computed: {
    formattedDate() {
      if (!this.comment.created_at) return '';
      const d = new Date(this.comment.created_at);
      return d.toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    canReport() {
      return !!this.currentUserId && !this.isOwn;
    },
  },

  watch: {
    'comment.likes_count'(val) { this.localLikesCount = val || 0; },
    'comment.dislikes_count'(val) { this.localDislikesCount = val || 0; },
    'comment.user_vote'(val) { this.localUserVote = val || null; },
  },

  mounted() {
    this.$nextTick(() => {
      const el = this.$refs.bodyEl;
      // +4px threshold to account for sub-pixel rounding in various browsers
      if (el && el.scrollHeight > el.clientHeight + 4) {
        this.showReadMore = true;
      } else {
        this.isCollapsed = false;
      }
    });
    document.addEventListener('click', this._onDocClick);
  },

  beforeUnmount() {
    document.removeEventListener('click', this._onDocClick);
  },

  methods: {
    _onDocClick(e) {
      if (this.showMenu && this.$refs.menuEl && !this.$refs.menuEl.contains(e.target)) {
        this.showMenu = false;
      }
    },

    isTextEmpty(html) {
      if (!html) return true;
      return html.replace(/<[^>]*>/g, '').trim() === '';
    },

    scrollToParent() {
      const parentEl = document.getElementById(`comment-${this.comment.parent_id}`);
      if (parentEl) {
        parentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        parentEl.classList.add('comment-item--highlight');
        setTimeout(() => parentEl.classList.remove('comment-item--highlight'), 1800);
      }
    },

    openEdit() {
      this.showMenu = false;
      this.editText = this.comment.text;
      this.isEditing = true;
      this.editError = null;
      // Uncollapse so the user can see the full text being edited
      this.isCollapsed = false;
    },

    cancelEdit() {
      this.isEditing = false;
      this.editText = '';
      this.editError = null;
    },

    async saveEdit() {
      if (this.isTextEmpty(this.editText) || this.isEditSaving) return;

      this.isEditSaving = true;
      this.editError = null;
      try {
        const recaptchaToken = await this.getRecaptchaToken('edit_comment');
        const response = await window.axios.put(
          `polls/${this.pollId}/comments/${this.comment.id}`,
          { text: this.editText, recaptcha_token: recaptchaToken }
        );
        const updated = response.data.data;
        this.$emit('edited', updated);
        this.isEditing = false;
        this.editText = '';
      } catch (error) {
        this.editError = error?.response?.data?.message || 'A apărut o eroare. Încearcă din nou.';
      } finally {
        this.isEditSaving = false;
      }
    },

    async handleVote(value) {
      if (this.isVoting) return;

      const token = window.localStorage.getItem('auth_token');
      if (!token) {
        // Emit up to trigger auth modal in parent
        this.$emit('need-auth');
        return;
      }

      this.isVoting = true;
      this.voteError = null;
      try {
        if (this.localUserVote === value) {
          // Revoke existing vote
          const resp = await window.axios.delete(
            `comments/${this.comment.id}/assessments`
          );
          this.localUserVote      = resp.data.user_vote;
          this.localLikesCount    = resp.data.likes_count;
          this.localDislikesCount = resp.data.dislikes_count;
        } else {
          // Set or switch vote
          const resp = await window.axios.post(
            `comments/${this.comment.id}/assessments`,
            { value }
          );
          this.localUserVote      = resp.data.user_vote;
          this.localLikesCount    = resp.data.likes_count;
          this.localDislikesCount = resp.data.dislikes_count;
        }
      } catch {
        this.voteError = 'Eroare la înregistrarea votului.';
        setTimeout(() => { this.voteError = null; }, 3000);
      } finally {
        this.isVoting = false;
      }
    },

    async deleteComment() {
      if (this.isDeleting) return;
      this.showMenu = false;
      this.isDeleting = true;
      this.deleteError = null;
      try {
        await window.axios.delete(`polls/${this.pollId}/comments/${this.comment.id}`);
        this.$emit('deleted', this.comment);
      } catch (error) {
        this.deleteError = error?.response?.data?.message || 'Eroare la ștergerea comentariului.';
        setTimeout(() => { this.deleteError = null; }, 4000);
      } finally {
        this.isDeleting = false;
      }
    },

    async reportComment() {
      if (this.isReporting || this.isReportedByMe) return;
      this.showMenu = false;
      this.isReporting = true;
      this.reportError = null;
      try {
        await window.axios.post('content-reports', {
          content_id: this.comment.id,
          content_type: 'comment',
        });
        this.$emit('reported', this.comment.id);
      } catch (error) {
        this.reportError = error?.response?.data?.message || 'Eroare la raportare.';
        setTimeout(() => { this.reportError = null; }, 4000);
      } finally {
        this.isReporting = false;
      }
    },
  },
};
</script>
